// Opal Funnel Studio shim.
//
// Activates only when the funnel is loaded with `?_studio=1`. It keeps
// the studio parent synced with the funnel cursor, accepts screen-jump
// requests from the parent, and lazily mounts the annotation overlay.

const FLAG_PARAM = "_studio";

let observer = null;
let lastEmittedScreenId = null;
let pendingScreenId = null;
let overlayPromise = null;
let overlay = null;
let annotationOverlayEnabled = false;

function isActive() {
  try {
    return new URLSearchParams(window.location.search).get(FLAG_PARAM) === "1";
  } catch {
    return false;
  }
}

function ensureObserver() {
  if (observer) return;
  const target = document.body;
  if (!target) {
    setTimeout(ensureObserver, 50);
    return;
  }
  observer = new MutationObserver(() => {
    queueMicrotask(() => {
      flushPendingGoto();
      emitCurrentScreen();
    });
  });
  observer.observe(target, { childList: true, subtree: true, characterData: true });
}

function emitCurrentScreen() {
  const screen = window.__funnelScreen;
  const cursor = window.__funnelCursor;
  const screenId = screen && typeof screen.id === "string" ? screen.id : null;
  if (!screenId || screenId === lastEmittedScreenId) return;
  lastEmittedScreenId = screenId;
  try {
    window.parent?.postMessage(
      { type: "opal-studio-screen", screenId, cursor: typeof cursor === "number" ? cursor : null },
      "*",
    );
  } catch {}
}

function handleMessage(ev) {
  if (!ev.data || typeof ev.data !== "object") return;
  if (ev.data.type === "opal-studio-goto") {
    gotoScreen(ev.data.screenId);
    return;
  }
  if (ev.data.kind === "opal-studio-annotation") {
    void handleAnnotationMessage(ev.data);
  }
}

async function handleAnnotationMessage(message) {
  if (message.op === "exit") {
    annotationOverlayEnabled = false;
    overlay?.exit();
    return;
  }
  if (message.op !== "enter" && !annotationOverlayEnabled) return;
  if (message.op === "enter") annotationOverlayEnabled = true;
  const controller = await loadOverlay();
  if (!annotationOverlayEnabled) {
    controller.exit();
    return;
  }
  if (message.op === "enter") {
    controller.enter(message.tool, Array.isArray(message.annotations) ? message.annotations : []);
  } else if (message.op === "set-tool") {
    controller.setTool(message.tool);
  } else if (message.op === "redraw") {
    controller.setAnnotations(Array.isArray(message.annotations) ? message.annotations : []);
  } else if (message.op === "flash") {
    controller.flash(message.annotationId);
  } else if (message.op === "delete") {
    controller.delete(message.annotationId);
  }
}

async function loadOverlay() {
  if (overlay) return overlay;
  if (!overlayPromise) {
    overlayPromise = import("./studio-annotation-overlay.js").then((mod) => mod.default ?? mod.overlay);
  }
  overlay = await overlayPromise;
  return overlay;
}

function gotoScreen(screenId) {
  if (typeof screenId !== "string" || !screenId) return;
  pendingScreenId = screenId;
  flushPendingGoto();
}

function flushPendingGoto() {
  if (pendingScreenId == null) return;
  const screens = window.__funnelScreens;
  const gotoFn = window.__funnelGoto;
  if (typeof gotoFn !== "function") return;
  let idx = Array.isArray(screens)
    ? screens.findIndex((screen) => screen && screen.id === pendingScreenId)
    : -1;
  if (idx < 0) {
    const order = window.__opalFunnel && window.__opalFunnel.screenOrder;
    if (Array.isArray(order)) idx = order.indexOf(pendingScreenId);
  }
  if (idx < 0) return;
  pendingScreenId = null;
  gotoFn(idx);
}

if (typeof window !== "undefined" && isActive()) {
  window.addEventListener("message", handleMessage);
  ensureObserver();
  emitCurrentScreen();
  window.__opalStudioAnnotationActive = true;
}
